import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { formatActivityContent } from '../../utils/activityFormatter';
import { useParams, useNavigate } from 'react-router-dom';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import DetailPageLayout from '../../components/crm/DetailPageLayout';
import activitiesService, { Activity } from '../../services/crm/activitiesService';
import NotesPanel from '../../components/crm/NotesPanel';
import { toast } from 'react-hot-toast';

const ActivityDetail: React.FC = () => {
  const { activityId = '' } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();

  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [editMeta, setEditMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ attendees: '', location: '', video_url: '' });

  useEffect(() => { document.title = 'CRM | Activity'; }, []);

  const load = useCallback(async () => {
    if (!activeOrgId || !activityId) return;
    setLoading(true); setError(null);
    try {
      const resp = await activitiesService.get(activityId, activeOrgId);
      setActivity(resp.data || null);
      if (!resp.data) setError('Activity not found');
      if (resp.data && resp.data.type === 'meeting') {
        setMetaForm({
          attendees: resp.data.attendees || '',
          location: resp.data.location || '',
          video_url: resp.data.video_url || ''
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, activityId]);

  const handleRsvp = useCallback(async (status: 'accepted' | 'tentative' | 'declined') => {
    try {
      if (!activeOrgId || !activityId) return;
      setRsvpLoading(true);
      await activitiesService.rsvpMeeting(activityId, activeOrgId, status);
      toast.success(`RSVP: ${status}`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to submit RSVP');
    } finally {
      setRsvpLoading(false);
    }
  }, [activeOrgId, activityId, load]);

  useEffect(() => { void load(); }, [load]);

  const meetingSummary = useMemo(() => {
    if (!activity) return null;
    if (!activity.content) return null;
    const idx = activity.content.indexOf('\n\n');
    return (idx > -1 ? activity.content.slice(0, idx) : activity.content).replace(/\n/g, ' ');
  }, [activity]);

  const downloadICS = useCallback(() => {
    if (!activity || activity.type !== 'meeting') return;
    const start = activity.due_at ? new Date(activity.due_at) : new Date(activity.created_at);
    const end = new Date(start.getTime() + 30 * 60000);
    const dt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const uid = `${activity.id}@explanatum.co.uk`;
    const summary = meetingSummary || 'Meeting';
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Explanatum CRM//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dt(new Date())}`,
      `DTSTART:${dt(start)}`,
      `DTEND:${dt(end)}`,
      `SUMMARY:${summary}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meeting.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [activity, meetingSummary]);

  const copyICS = useCallback(async () => {
    if (!activity || activity.type !== 'meeting') return;
    const start = activity.due_at ? new Date(activity.due_at) : new Date(activity.created_at);
    const end = new Date(start.getTime() + 30 * 60000);
    const dt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const uid = `${activity.id}@explanatum.co.uk`;
    const summary = meetingSummary || 'Meeting';
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Explanatum CRM//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dt(new Date())}`,
      `DTSTART:${dt(start)}`,
      `DTEND:${dt(end)}`,
      `SUMMARY:${summary}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    try {
      await navigator.clipboard.writeText(ics);
      toast.success('ICS copied to clipboard');
    } catch {
      toast.error('Failed to copy ICS');
    }
  }, [activity, meetingSummary]);

  const activityTitle = useMemo(() => {
    if (!activity) return 'Activity';
    return activity.type ? activity.type.charAt(0).toUpperCase() + activity.type.slice(1) : 'Activity';
  }, [activity]);

  return (
    <DetailPageLayout
      title={activityTitle}
      backLink="/crm#activities"
      backLabel="Back to Activities"
    >
      <div className="bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-100 space-y-4">
        {!activeOrgId && (
          <div className="text-sm text-gray-600">Select or create an organization above to begin.</div>
        )}
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div>Loading…</div>}

        {activity && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div><span className="text-gray-500">Type:</span> {activity.type}</div>
                <div><span className="text-gray-500">Entity:</span> {activity.entity_type} ({activity.entity_id})</div>
                {activity.site_id && (<div><span className="text-gray-500">Site:</span> {activity.site_id}</div>)}
              </div>
              <div>
                <div><span className="text-gray-500">Created:</span> {new Date(activity.created_at).toLocaleString()}</div>
                <div><span className="text-gray-500">Updated:</span> {new Date(activity.updated_at).toLocaleString()}</div>
                {activity.due_at && (<div><span className="text-gray-500">Due:</span> {new Date(activity.due_at).toLocaleString()}</div>)}
              </div>
            </div>
            {activity.type === 'meeting' && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <button className="border rounded px-3 py-1" onClick={downloadICS}>Download ICS</button>
                  <button className="border rounded px-3 py-1" onClick={copyICS}>Copy ICS</button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    className="px-3 py-1 bg-green-600 text-white rounded disabled:opacity-60"
                    disabled={rsvpLoading}
                    onClick={() => handleRsvp('accepted')}
                  >
                    Accept
                  </button>
                  <button
                    className="px-3 py-1 bg-yellow-500 text-white rounded disabled:opacity-60"
                    disabled={rsvpLoading}
                    onClick={() => handleRsvp('tentative')}
                  >
                    Tentative
                  </button>
                  <button
                    className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-60"
                    disabled={rsvpLoading}
                    onClick={() => handleRsvp('declined')}
                  >
                    Decline
                  </button>
                  <button
                    className="px-3 py-1 border rounded"
                    onClick={() => setEditMeta(v => !v)}
                  >
                    {editMeta ? 'Cancel edit' : 'Edit details'}
                  </button>
                </div>
                {/* Meeting status */}
                <div className="mt-3 text-sm">
                  {((activity as any).status || (activity as any).outcome) ? (
                    <div><span className="text-gray-500">RSVP/Outcome:</span> {String((activity as any).status || (activity as any).outcome)}</div>
                  ) : (
                    <div className="text-gray-500">No RSVP or outcome set.</div>
                  )}
                </div>
                {/* Meeting metadata display / edit */}
                {!editMeta ? (
                  <div className="mt-3 text-sm space-y-1">
                    {activity.attendees && <div><span className="text-gray-500">Attendees:</span> {activity.attendees}</div>}
                    {activity.location && <div><span className="text-gray-500">Location:</span> {activity.location}</div>}
                    {activity.video_url && (
                      <div>
                        <span className="text-gray-500">Video:</span> <a className="text-blue-600 hover:underline" href={activity.video_url} target="_blank" rel="noreferrer">Join</a>
                      </div>
                    )}
                    {!activity.attendees && !activity.location && !activity.video_url && (
                      <div className="text-gray-500">No meeting details.</div>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <label className="block">
                      <div className="text-gray-700">Attendees</div>
                      <input className="border rounded w-full p-2" value={metaForm.attendees} onChange={e=> setMetaForm({ ...metaForm, attendees: e.target.value })} />
                    </label>
                    <label className="block">
                      <div className="text-gray-700">Location</div>
                      <input className="border rounded w-full p-2" value={metaForm.location} onChange={e=> setMetaForm({ ...metaForm, location: e.target.value })} />
                    </label>
                    <label className="block">
                      <div className="text-gray-700">Video URL</div>
                      <input className="border rounded w-full p-2" value={metaForm.video_url} onChange={e=> setMetaForm({ ...metaForm, video_url: e.target.value })} />
                    </label>
                    <div className="md:col-span-3 flex items-center justify-end gap-2">
                      <button className="px-3 py-1 border rounded" onClick={() => setEditMeta(false)}>Cancel</button>
                      <button
                        className="px-3 py-1 bg-blue-600 text-white rounded"
                        onClick={async () => {
                          if (!activity || !activeOrgId) return;
                          try {
                            await activitiesService.update(activity.id, { organization_id: activeOrgId, attendees: metaForm.attendees || undefined, location: metaForm.location || undefined, video_url: metaForm.video_url || undefined });
                            toast.success('Saved');
                            setEditMeta(false);
                            await load();
                          } catch (e: any) {
                            toast.error(e?.message || 'Failed to save');
                          }
                        }}
                      >Save</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {activity.content && (
              <div className="p-3 border rounded whitespace-pre-wrap">{formatActivityContent(activity as any)}</div>
            )}

            <div className="pt-6 border-t">
              <NotesPanel parentActivityId={activityId} title="Notes on this activity" />
            </div>
          </>
        )}
      </div>
    </DetailPageLayout>
  );
};

export default ActivityDetail;
